import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { getFilteredTourSteps, getDefaultTourState } from './tourConfig';

const TourContext = createContext(null);

export const TourProvider = ({ children }) => {
  const { user, api } = useAuth();
  const location = useLocation();
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourState, setTourState] = useState(getDefaultTourState);
  const [isLoading, setIsLoading] = useState(true);
  const [gettingStartedComplete, setGettingStartedComplete] = useState(false);

  // Check if we're on onboarding-related pages where tour should not auto-start
  const isOnboardingPage = location.pathname === '/onboarding' || 
                           location.pathname === '/free-trial' || 
                           location.pathname === '/verify-email' ||
                           location.pathname === '/getting-started';
  
  // Tour should only auto-start on Dashboard
  const isDashboardPage = location.pathname === '/dashboard';

  // Get filtered tour steps based on user role and tenant industry
  const tourSteps = useMemo(() => {
    const isAdmin = user?.role === 'admin';
    const industry = user?.industry || 'crane_hire';
    return getFilteredTourSteps(isAdmin, industry);
  }, [user?.role, user?.industry]);

  // Fetch tour state from backend on mount
  useEffect(() => {
    const fetchTourState = async () => {
      if (!user || !api) {
        setIsLoading(false);
        return;
      }

      try {
        const [onboardingRes, checklistRes] = await Promise.all([
          api.get('/users/me/onboarding'),
          api.get('/onboarding/checklist-status').catch(() => ({ data: { all_required_complete: false } }))
        ]);
        
        if (onboardingRes.data) {
          setTourState(prev => ({
            ...prev,
            mainTourCompleted: onboardingRes.data.main_tour_completed || false,
            mainTourSkipped: onboardingRes.data.main_tour_skipped || false
          }));
          
          // Check if Getting Started is complete (wizard done + all checklist items)
          const wizardComplete = onboardingRes.data.wizard_completed || false;
          const checklistComplete = checklistRes.data?.all_required_complete || false;
          const gsComplete = wizardComplete && checklistComplete;
          setGettingStartedComplete(gsComplete);
          
          // Only auto-start tour on Dashboard page, after Getting Started is complete
          // and user hasn't completed/skipped the tour
          if (!onboardingRes.data.main_tour_completed && 
              !onboardingRes.data.main_tour_skipped && 
              isDashboardPage && 
              gsComplete) {
            setTimeout(() => {
              setIsTourActive(true);
            }, 1000);
          }
        }
      } catch (error) {
        // If endpoint doesn't exist yet, check localStorage as fallback
        console.log('Onboarding endpoint not available, using defaults');
        const localState = localStorage.getItem('equipr_tour_state');
        if (localState) {
          try {
            const parsed = JSON.parse(localState);
            setTourState(parsed);
          } catch (e) {
            // Invalid JSON
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTourState();
  }, [user, api, isDashboardPage]);

  // Save tour state to backend
  const saveTourState = useCallback(async (newState) => {
    setTourState(newState);
    
    // Save to localStorage as backup
    localStorage.setItem('equipr_tour_state', JSON.stringify(newState));
    
    // Try to save to backend
    if (api) {
      try {
        await api.patch('/users/me/onboarding', {
          main_tour_completed: newState.mainTourCompleted,
          main_tour_skipped: newState.mainTourSkipped
        });
      } catch (error) {
        console.log('Could not save tour state to backend:', error);
      }
    }
  }, [api]);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsTourActive(true);
  }, []);

  const completeTour = useCallback(() => {
    setIsTourActive(false);
    setCurrentStep(0);
    saveTourState({
      ...tourState,
      mainTourCompleted: true
    });
  }, [tourState, saveTourState]);

  const nextStep = useCallback(() => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tour complete
      completeTour();
    }
  }, [currentStep, tourSteps.length, completeTour]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const skipTour = useCallback(() => {
    setIsTourActive(false);
    setCurrentStep(0);
    saveTourState({
      ...tourState,
      mainTourSkipped: true
    });
  }, [tourState, saveTourState]);

  const resetTour = useCallback(() => {
    const newState = getDefaultTourState();
    setTourState(newState);
    saveTourState(newState);
  }, [saveTourState]);

  const currentTourStep = tourSteps[currentStep];

  return (
    <TourContext.Provider value={{
      isTourActive,
      currentStep,
      totalSteps: tourSteps.length,
      currentTourStep,
      tourState,
      isLoading,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      resetTour,
      hasCompletedTour: tourState.mainTourCompleted,
      hasSkippedTour: tourState.mainTourSkipped
    }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export default TourContext;
